import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { appPath } from '../utils/appRouting';
import { 
  Mail, 
  RefreshCw, 
  CheckCircle,
  AlertCircle,
  LogOut
} from 'lucide-react';
import { auth } from '../firebase';
import { sendEmailVerification, signOut } from 'firebase/auth';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const user = auth.currentUser;

  // Se non c'è utente, torna al login
  useEffect(() => {
    if (!user) {
      navigate(appPath('/login'));
    }
  }, [user, navigate]);

  // Se l'utente è già verificato, vai alla dashboard
  useEffect(() => {
    if (user?.emailVerified) {
      navigate(appPath('/dashboard'));
    }
  }, [user, navigate]);

  const handleResendEmail = async () => {
    if (!user) return;
    
    setLoading(true);
    setError('');
    setSent(false);

    try {
      await sendEmailVerification(user);
      setSent(true);
    } catch (err) {
      console.error('Error sending verification email:', err);
      if (err.code === 'auth/too-many-requests') {
        setError('Troppe richieste. Attendi qualche minuto prima di riprovare.');
      } else {
        setError('Errore nell\'invio dell\'email. Riprova più tardi.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;
    
    setChecking(true);
    setError('');

    try {
      // Ricarica l'utente per ottenere lo stato aggiornato
      await user.reload();
      
      if (user.emailVerified) {
        navigate(appPath('/dashboard'));
      } else {
        setError('Email non ancora verificata. Controlla la tua casella di posta.');
      }
    } catch (err) {
      console.error('Error checking verification:', err);
      setError('Errore nella verifica. Riprova.');
    } finally {
      setChecking(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate(appPath('/login'));
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-brand/10 to-background flex items-center justify-center p-4">
      <div className="max-w-md w-full ds-panel p-8 text-center">
        {/* Icon */}
        <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-10 h-10 text-amber-600" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verifica la tua email
        </h2>
        
        <p className="text-gray-600 mb-6">
          Abbiamo inviato un'email di verifica a:<br />
          <strong className="text-gray-900">{user.email}</strong>
        </p>

        {/* Instructions */}
        <div className="bg-muted/40 rounded-xl p-4 mb-6 text-left border border-border">
          <p className="text-sm text-foreground">
            <strong>Come fare:</strong>
          </p>
          <ol className="text-sm text-muted-foreground mt-2 space-y-1 list-decimal list-inside">
            <li>Controlla la tua casella email</li>
            <li>Clicca sul link di verifica</li>
            <li>Torna qui e clicca "Ho verificato"</li>
          </ol>
        </div>

        {/* Success message */}
        {sent && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">Email inviata! Controlla la tua casella di posta.</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Buttons */}
        <div className="space-y-3">
          <Button
            onClick={handleCheckVerification}
            disabled={checking}
            className="w-full ds-btn-gradient h-12"
          >
            {checking ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Verifico...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Ho verificato l'email
              </span>
            )}
          </Button>

          <Button
            onClick={handleResendEmail}
            disabled={loading}
            variant="outline"
            className="w-full h-12"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Invio in corso...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Reinvia email di verifica
              </span>
            )}
          </Button>

          <button
            onClick={handleLogout}
            className="w-full text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-2 py-2"
          >
            <LogOut className="w-4 h-4" />
            Esci e usa un'altra email
          </button>
        </div>

        {/* Help text */}
        <p className="mt-6 text-xs text-gray-500">
          Non trovi l'email? Controlla la cartella spam o posta indesiderata.
        </p>
      </div>
    </div>
  );
};

export default VerifyEmail;
